import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IoAdd, IoVideocam } from "react-icons/io5";
import AppDeviceCard from "../../components/device/app/AppDeviceCard";
import { CardSkeletons } from "../../components/device/DeviceCard";
import { useToggleTop } from "../../components/common/ToggleTop";
import { usePropertyRole } from "../../hooks/usePropertyOwner";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { apiMessage } from "../../api/apiMessage";
import { User } from "../../storage/store";
import AddCamDevice from "./AddCamDevice";
import { CAMERA_PATHS } from "./paths";
import { EzvizError, accessTokenOf, areaDomainOf, camerasOfHome, codesOf, deviceNameOf, listEzvizDevices } from "./cameraUtils";
import "../../assets/styles/app-camera.css";

const ONLINE = 1;

// The camera list (app/screens/cam/CameraDevices.js): the Camera tab's body
// and the Devices tab's CAMERAS chip. The API's token opens EZVIZ's device
// list; each device opens its cameras. Only owners add cameras; arriving with
// { add: true } (Home's "+" Add Camera) opens the add dialog.
function CameraDevices() {
  const history = useHistory();
  const { pathname, state } = useLocation();
  const toast = useToggleTop();
  const alive = useAlive();
  const role = usePropertyRole();
  // null while loading; { devices, accessToken, area } | { error }
  const [result, setResult] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setResult(null);
    const propertyId = User.getPropertyId();
    let accessToken;
    let area;
    try {
      const reply = await UserService.getHIKAccessToken(propertyId);
      accessToken = accessTokenOf(reply);
      area = areaDomainOf(reply);
    } catch (error) {
      // the API's own reason when it gives one (no access, camera service down)
      if (alive.current) setResult({ error: apiMessage(error, "Couldn't reach your camera account. Please try again.") });
      return;
    }
    // No token: the home has no cameras yet (its EZVIZ account comes with the first one).
    if (!accessToken) {
      if (alive.current) setResult({ devices: [], accessToken: null, area });
      return;
    }
    try {
      const [devices, codes] = await Promise.all([
        listEzvizDevices(accessToken, area),
        UserService.getHIKCodes(propertyId).then(codesOf),
      ]);
      if (alive.current) setResult({ devices: camerasOfHome(devices, codes), accessToken, area });
    } catch (error) {
      if (!alive.current) return;
      setResult({
        error:
          error instanceof EzvizError ? `EZVIZ: ${error.message}` : "Couldn't load your cameras. Please try again.",
      });
    }
  }, [alive]);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => {
    if (role !== "owner") {
      toast("You have to be a owner to add a camera.", "red");
      return;
    }
    setAdding(true);
  };

  // Once the home's role is known; the state is cleared so Back doesn't open it again.
  const addOnArrival = Boolean(state?.add);
  useEffect(() => {
    if (!addOnArrival || !role) return;
    history.replace(pathname, { ...state, add: false });
    startAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOnArrival, role]);

  const open = (device) => history.push(CAMERA_PATHS.DEVICE, { device, accessToken: result.accessToken, area: result.area });

  let content;
  if (!result) {
    content = (
      <div className="hx-grid" aria-label="Loading">
        <CardSkeletons count={3} />
      </div>
    );
  } else if (result.error) {
    content = (
      <div className="hx-empty" role="alert">
        {result.error}
        <button type="button" className="hx-btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  } else if (result.devices.length) {
    content = (
      <div className="hx-grid">
        {result.devices.map((device) => {
          const name = deviceNameOf(device);
          const online = device.status === ONLINE;
          const meta = [online ? "Online" : "Offline", device.deviceType].filter(Boolean).join(" | ");
          return (
            <AppDeviceCard
              key={device.deviceSerial}
              name={name}
              meta={meta}
              type="camera"
              active={false}
              centerIcon
              ariaLabel={`${name}, ${meta}. Open cameras`}
              onOpen={() => open(device)}
            />
          );
        })}
      </div>
    );
  } else {
    content = (
      <div className="hx-empty-page">
        <IoVideocam aria-hidden="true" />
        <p>No camera devices found</p>
        <span>Add a Hikvision or EZVIZ camera with its serial number and verification code.</span>
        {role === "owner" ? (
          <div className="hx-empty-actions">
            <button type="button" className="hx-btn" onClick={startAdd}>
              Add camera
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="hx-cam-devices">
      {result && !result.error ? (
        <div className="hx-cam-head">
          <button type="button" className="hx-cam-add" onClick={startAdd} aria-label="Add camera">
            Add <IoAdd aria-hidden="true" />
          </button>
        </div>
      ) : null}
      {content}
      <AddCamDevice
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={() => {
          setAdding(false);
          load();
        }}
      />
    </div>
  );
}

export default CameraDevices;
